import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Ship, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
interface BoatSpecs {
  Year: string;
  Make: string;
  Model: string;
  Type: string;
  Length: string;
  Beam: string;
  'Person Capacity': string;
  'Fuel Capacity': string;
  Color: string;
  'Motor Brand': string;
  Horsepower: string;
}
interface BoatCardProps {
  name: string;
  type: string;
  length: string;
  motor: string;
  capacity: string;
  price: string;
  specs: BoatSpecs;
  image?: string;
  isPlaceholder?: boolean;
}
function BoatCard({
  name,
  type,
  length,
  motor,
  price,
  specs,
  image,
  isPlaceholder = false
}: BoatCardProps) {
  const [showSpecs, setShowSpecs] = useState(false);
  return <div className="bg-white rounded-2xl overflow-hidden shadow-md border border-navy-100 flex flex-col h-full" data-id="element-82">
      {/* Image Slot */}
      <div className="aspect-video bg-navy-50 relative flex items-center justify-center" data-id="element-83">
        {image ? <img src={image} alt={name} className="absolute inset-0 w-full h-full object-cover" data-id="element-84" /> : <Ship className="w-16 h-16 text-navy-200" data-id="element-85" />}
        <div className="absolute inset-0 border-b border-navy-100" data-id="element-86"></div>
        {isPlaceholder && <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-navy-500" data-id="element-87">
            Coming Soon
          </div>}
      </div>

      <div className="p-6 flex flex-col flex-grow" data-id="element-88">
        <h3 className={`text-2xl font-heading font-bold mb-4 ${isPlaceholder ? 'text-navy-400' : 'text-navy'}`} data-id="element-89">
          {name}
        </h3>

        {/* Key Specs */}
        <div className="grid grid-cols-2 gap-y-3 mb-6" data-id="element-90">
          <div data-id="element-91">
            <p className="text-xs text-navy-400 uppercase tracking-wider font-semibold" data-id="element-92">
              Type
            </p>
            <p className={`font-medium ${isPlaceholder ? 'text-navy-300' : 'text-navy-700'}`} data-id="element-93">
              {type}
            </p>
          </div>
          <div data-id="element-94">
            <p className="text-xs text-navy-400 uppercase tracking-wider font-semibold" data-id="element-95">
              Length
            </p>
            <p className={`font-medium ${isPlaceholder ? 'text-navy-300' : 'text-navy-700'}`} data-id="element-96">
              {length}
            </p>
          </div>
          <div data-id="element-97">
            <p className="text-xs text-navy-400 uppercase tracking-wider font-semibold" data-id="element-98">
              Motor
            </p>
            <p className={`font-medium ${isPlaceholder ? 'text-navy-300' : 'text-navy-700'}`} data-id="element-99">
              {motor}
            </p>
          </div>
          <div data-id="element-100">
            <p className="text-xs text-navy-400 uppercase tracking-wider font-semibold" data-id="element-101">
              Price
            </p>
            <p className={`font-medium ${isPlaceholder ? 'text-navy-300' : 'text-navy-700'}`} data-id="element-102">
              {price}
            </p>
          </div>
        </div>

        {/* Full Specs Toggle */}
        <div className="mt-auto" data-id="element-103">
          <button onClick={() => setShowSpecs(!showSpecs)} className="flex items-center justify-between w-full py-3 border-t border-navy-100 text-sm font-medium text-copper hover:text-copper-600 transition-colors" data-id="element-104">
            <span data-id="element-105">{showSpecs ? 'Hide Full Specs' : 'View Full Specs'}</span>
            {showSpecs ? <ChevronUp className="w-4 h-4" data-id="element-106" /> : <ChevronDown className="w-4 h-4" data-id="element-107" />}
          </button>

          {/* Expanded Specs Table */}
          {showSpecs && <div className="py-4 border-t border-navy-100 text-sm animate-in slide-in-from-top-2 duration-200" data-id="element-108">
              <dl className="space-y-2" data-id="element-109">
                {Object.entries(specs).map(([key, value]) => <div key={key} className="flex justify-between border-b border-navy-50 pb-1 last:border-0" data-id="element-110">
                    <dt className="text-navy-500" data-id="element-111">{key}</dt>
                    <dd className={`font-medium text-right ${isPlaceholder ? 'text-navy-300' : 'text-navy-800'}`} data-id="element-112">
                      {value}
                    </dd>
                  </div>)}
              </dl>
            </div>}

          {/* Actions */}
          <div className="mt-6 pt-4 border-t border-navy-100" data-id="element-113">
            <Button variant="secondary" className="w-full py-2.5 text-sm" onClick={() => document.getElementById('contact')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-114">
              Get More Info
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export function InventoryGrid() {
  const realBoat = {
    name: '2025 Bennington 22SSR',
    type: 'Pontoon',
    length: '22\'11"',
    motor: 'Mercury 150 HP',
    capacity: '12',
    image: 'https://cdn.magicpatterns.com/uploads/iPgjyVF8cstor2UZM3ZXyV/IMG_7479.jpg',
    price: '$44,690',
    specs: {
      Year: '2025',
      Make: 'Bennington',
      Model: '22SSR',
      Type: 'Pontoon',
      Length: '22\'11"',
      Beam: '8\'6"',
      'Person Capacity': '12',
      'Fuel Capacity': '33 gallons',
      Color: 'Anthracite',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const secondBoat = {
    name: '2024 Sea Ray 230 SPO',
    type: 'Bow Rider',
    length: '26\'1"',
    motor: 'Mercury 200 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/pW9XGcDtKsZRSchDhGrNrv/CMd6IydQ.jpg',
    price: '$63,750',
    specs: {
      Year: '2024',
      Make: 'Sea Ray',
      Model: '230 SPO',
      Type: 'Bow Rider',
      Length: '26\'1"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '47 gallons',
      Color: 'White',
      'Motor Brand': 'Mercury',
      Horsepower: '200 HP'
    }
  };
  const thirdBoat = {
    name: '2025 Barletta Aria 24',
    type: 'Pontoon',
    length: '23\'10"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/n4gA8LGuqN9RadzqZXLqGH/2025_Barletta_Navy_11.jpg',
    price: '$48,660',
    specs: {
      Year: '2025',
      Make: 'Barletta',
      Model: 'Aria 24',
      Type: 'Pontoon',
      Length: '23\'10"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Blue Sapphire',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const fourthBoat = {
    name: '2025 Highfield 660',
    type: 'Rigid Inflatable',
    length: '21\'10"',
    motor: 'Flux Motor',
    capacity: '14',
    image: 'https://cdn.magicpatterns.com/uploads/ox8ttcbTcwcZKDwLgL5ZLX/2025_Highfield_6.jpg',
    price: '$77,400',
    specs: {
      Year: '2025',
      Make: 'Highfield',
      Model: '660',
      Type: 'Rigid Inflatable',
      Length: '21\'10"',
      Beam: '8\'5"',
      'Person Capacity': '14',
      'Fuel Capacity': 'N/A',
      Color: 'Gray',
      'Motor Brand': 'Flux',
      Horsepower: '115 HP'
    }
  };
  const fifthBoat = {
    name: '2025 Barletta Aria 24',
    type: 'Pontoon',
    length: '23\'10"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/kP5oRBSwTjZoRdyZevGBJ5/2025_Barletta_Black_1.jpg',
    price: '$48,660',
    specs: {
      Year: '2025',
      Make: 'Barletta',
      Model: 'Aria 24',
      Type: 'Pontoon',
      Length: '23\'10"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Black Diamond',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const sixthBoat = {
    name: '2025 Sea Ray 210 SPO',
    type: 'Bow Rider',
    length: '23\'11"',
    motor: 'Mercury 150 HP',
    capacity: '12',
    image: 'https://cdn.magicpatterns.com/uploads/nwZADvH4591XfxeWmW1CQ7/IMG_7516.jpg',
    price: '$46,900',
    specs: {
      Year: '2025',
      Make: 'Sea Ray',
      Model: '210 SPO',
      Type: 'Bow Rider',
      Length: '23\'11"',
      Beam: '8\'6"',
      'Person Capacity': '12',
      'Fuel Capacity': '37 gallons',
      Color: 'White',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const seventhBoat = {
    name: '2025 Regal LX2',
    type: 'Bow Rider',
    length: "24'",
    motor: 'Mercury 200 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/4Dqw9bzQFoyayV1n5UtQxE/IMG_7452.jpg',
    price: '$62,100',
    specs: {
      Year: '2025',
      Make: 'Regal',
      Model: 'LX2',
      Type: 'Bow Rider',
      Length: "24'",
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '56 gallons',
      Color: 'Med Blue',
      'Motor Brand': 'Mercury',
      Horsepower: '200 HP'
    }
  };
  const eighthBoat = {
    name: '2025 Regal LX2',
    type: 'Bow Rider',
    length: "24'",
    motor: 'Mercury 200 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/n8phEsRmPUtMKqHiHDk3Sn/2025_Regal_LX2_7841_11.jpg',
    price: '$62,100',
    specs: {
      Year: '2025',
      Make: 'Regal',
      Model: 'LX2',
      Type: 'Bow Rider',
      Length: "24'",
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '56 gallons',
      Color: 'Steel Gray',
      'Motor Brand': 'Mercury',
      Horsepower: '200 HP'
    }
  };
  const ninthBoat = {
    name: '2025 Regal LX2',
    type: 'Bow Rider',
    length: "24'",
    motor: 'Yamaha 200 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/d7cT8ADF61PtxyWEkcR52N/IMG_7503.jpg',
    price: '$67,485',
    specs: {
      Year: '2025',
      Make: 'Regal',
      Model: 'LX2',
      Type: 'Bow Rider',
      Length: "24'",
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '56 gallons',
      Color: 'Black',
      'Motor Brand': 'Yamaha',
      Horsepower: '200 HP'
    }
  };
  const tenthBoat = {
    name: '2025 Harris Cruiser 230 Fish',
    type: 'Pontoon',
    length: '23\'6"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/mWdGBVpLnSdSjYGZFNTjjE/2025_Harris_Fishing_1.jpg',
    price: '$43,250',
    specs: {
      Year: '2025',
      Make: 'Harris',
      Model: 'Cruiser 230 Fish',
      Type: 'Pontoon',
      Length: '23\'6"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Midnight Blue Metallic',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const eleventhBoat = {
    name: '2025 Harris Cruiser 230',
    type: 'Pontoon',
    length: '23\'6"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/vHrVuz2woCQ22BjNuFLNNL/2025_Harris_7078.jpg',
    price: '$42,295',
    specs: {
      Year: '2025',
      Make: 'Harris',
      Model: 'Cruiser 230',
      Type: 'Pontoon',
      Length: '23\'6"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Silver',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const twelfthBoat = {
    name: '2025 Harris Cruiser 230',
    type: 'Pontoon',
    length: '23\'6"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/6ZDDs1H3vyyftYsSASuPRa/2025_Harris_7080_8.jpg',
    price: '$42,295',
    specs: {
      Year: '2025',
      Make: 'Harris',
      Model: 'Cruiser 230',
      Type: 'Pontoon',
      Length: '23\'6"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Ebony Metallic',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const thirteenthBoat = {
    name: '2025 Harris Cruiser 230',
    type: 'Pontoon',
    length: '23\'6"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/bVr12sLwTBbFr8FzzR4GZR/IMG_7479_(1).jpg',
    price: '$42,295',
    specs: {
      Year: '2025',
      Make: 'Harris',
      Model: 'Cruiser 230',
      Type: 'Pontoon',
      Length: '23\'6"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Sterling Gray Metallic',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const fourteenthBoat = {
    name: '2025 Harris Cruiser 230',
    type: 'Pontoon',
    length: '23\'6"',
    motor: 'Mercury 150 HP',
    capacity: '13',
    image: 'https://cdn.magicpatterns.com/uploads/cnDA9jZ4EqVaDkKZfsynT2/IMG_7479_(2).jpg',
    price: '$42,295',
    specs: {
      Year: '2025',
      Make: 'Harris',
      Model: 'Cruiser 230',
      Type: 'Pontoon',
      Length: '23\'6"',
      Beam: '8\'6"',
      'Person Capacity': '13',
      'Fuel Capacity': '50 gallons',
      Color: 'Silver',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const fifteenthBoat = {
    name: '2025 Bennington 22SSR',
    type: 'Pontoon',
    length: '22\'11"',
    motor: 'Mercury 150 HP',
    capacity: '12',
    image: 'https://cdn.magicpatterns.com/uploads/mUTmdLsSsrxGDjHVZB76JA/IMG_7479_(3).jpg',
    price: '$44,690',
    specs: {
      Year: '2025',
      Make: 'Bennington',
      Model: '22SSR',
      Type: 'Pontoon',
      Length: '22\'11"',
      Beam: '8\'6"',
      'Person Capacity': '12',
      'Fuel Capacity': '33 gallons',
      Color: 'Metallic Silver',
      'Motor Brand': 'Mercury',
      Horsepower: '150 HP'
    }
  };
  const sixteenthBoat = {
    name: '2025 SeaPro FLX 225',
    type: 'Center Console',
    length: '21\'9"',
    motor: 'Mercury 250 HP',
    capacity: '9',
    image: 'https://cdn.magicpatterns.com/uploads/hd4AcmRfYAW33D2Bh5YSvQ/IMG_7479_(4).jpg',
    price: '$77,530',
    specs: {
      Year: '2025',
      Make: 'SeaPro',
      Model: 'FLX 225',
      Type: 'Center Console',
      Length: '21\'9"',
      Beam: '8\'6"',
      'Person Capacity': '9',
      'Fuel Capacity': '85 gallons',
      Color: 'White',
      'Motor Brand': 'Mercury',
      Horsepower: '250 HP'
    }
  };
  return <section id="inventory" className="py-20 px-6 md:px-12 bg-cream" data-id="element-115">
      <div className="max-w-7xl mx-auto" data-id="element-116">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-117">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-118">
            Available Pre-Owned Boats on Lake George
          </h2>
          <p className="text-navy-600 text-lg" data-id="element-119">
            Browse our selection of pre-owned boats for sale on Lake George,
            including professionally maintained freshwater fleet vessels.
          </p>
        </div>

        <InventoryCarousel boats={[realBoat, secondBoat, thirdBoat, fourthBoat, fifthBoat, sixthBoat, seventhBoat, eighthBoat, ninthBoat, tenthBoat, eleventhBoat, twelfthBoat, thirteenthBoat, fourteenthBoat, fifteenthBoat, sixteenthBoat]} data-id="element-120" />
      </div>
    </section>;
}
interface InventoryCarouselProps {
  boats: BoatCardProps[];
}
function InventoryCarousel({
  boats
}: InventoryCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const {
      scrollLeft,
      scrollWidth,
      clientWidth
    } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);
  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, {
      passive: true
    });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);
  const scrollByPage = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -el.clientWidth : el.clientWidth,
      behavior: 'smooth'
    });
  };
  return <div className="relative" data-id="element-121">
      <div ref={scrollRef} className="flex items-center justify-start gap-0 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 mx-0 px-0 md:-mx-2 md:px-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-id="element-122">
        {boats.map((boat, i) => <div key={i} className="snap-center shrink-0 basis-full md:basis-[calc((100%-2rem)/2)] lg:basis-[calc((100%-4rem)/3)]" data-id="element-123">
            <BoatCard {...boat} data-id="element-124" />
          </div>)}
      </div>

      <button type="button" onClick={() => scrollByPage('left')} disabled={!canScrollLeft} aria-label="Scroll to previous boats" className="hidden md:flex absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white shadow-lg border border-navy-100 items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed disabled:text-navy-300 disabled:hover:bg-white disabled:hover:text-navy-300" data-id="element-125">
        <ChevronLeft className="w-5 h-5" data-id="element-126" />
      </button>
      <button type="button" onClick={() => scrollByPage('right')} disabled={!canScrollRight} aria-label="Scroll to next boats" className="hidden md:flex absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white shadow-lg border border-navy-100 items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed disabled:text-navy-300 disabled:hover:bg-white disabled:hover:text-navy-300" data-id="element-127">
        <ChevronRight className="w-5 h-5" data-id="element-128" />
      </button>

      {/* Mobile scroll controls (below the listing) */}
      <div className="flex md:hidden justify-center gap-4 mt-6" data-id="element-129">
        <button type="button" onClick={() => scrollByPage('left')} disabled={!canScrollLeft} aria-label="Scroll to previous boats" className="w-11 h-11 rounded-full bg-white shadow-lg border border-navy-100 flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed disabled:text-navy-300 disabled:hover:bg-white disabled:hover:text-navy-300" data-id="element-130">
          <ChevronLeft className="w-5 h-5" data-id="element-131" />
        </button>
        <button type="button" onClick={() => scrollByPage('right')} disabled={!canScrollRight} aria-label="Scroll to next boats" className="w-11 h-11 rounded-full bg-white shadow-lg border border-navy-100 flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed disabled:text-navy-300 disabled:hover:bg-white disabled:hover:text-navy-300" data-id="element-132">
          <ChevronRight className="w-5 h-5" data-id="element-133" />
        </button>
      </div>
    </div>;
}